import Head from 'next/head';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import { useSelector } from 'react-redux';
import SEO from 'next-seo.config';
import { authSelector } from 'store/auth';
import SidebarLayout from 'layouts/SidebarLayout';
import Typo from 'components/Typo';
import BrokerCard from './BrokerCard';
import DirectoryBreadcrumb from './DirectoryBreadcrumb';
import DirectoryNotes from './DirectoryNotes';
import DirectoryStories from './DirectoryStories';
import {
    MORTGAGE_BROKERS_PATH,
    pageUpdatedAt,
    topicCopy,
    topicIntro,
    topicLabel,
    topicPath,
    topicSchema,
} from 'utils/directory';
import safeHtml, { jsonLd } from 'utils/safe-html';
import styles from 'styles/modules/BrokerDirectory.module.scss';

// A topic page: brokers listed through approved coverage on the topic.
export default function TopicPage({ page }) {
    const { isAuthenticated } = useSelector(authSelector);
    const { topic, brokers, stories } = page;
    const { title, heading, description, intro } = topicCopy(page);
    const path = topicPath(topic.slug, topic.underTopic);
    const url = `${process.env.HOST}${path}`;

    return (
        <SidebarLayout isAuthenticated={isAuthenticated} breadcrumb={false}>
            <NextSeo
                {...SEO}
                title={title}
                titleTemplate="%s"
                description={description}
                canonical={url}
                noindex={!page.indexable}
                openGraph={{ ...SEO.openGraph, title, description, url }}
            />
            <Head>
                <script
                    key="directory-jsonld"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: jsonLd(topicSchema(page, process.env.HOST)),
                    }}
                />
            </Head>

            <div className={styles.wrapper}>
                <DirectoryBreadcrumb
                    items={[
                        ['Newspage', '/'],
                        ['Mortgage brokers', MORTGAGE_BROKERS_PATH],
                        [topicLabel(topic.name), path],
                    ]}
                />
                <div className={styles.head}>
                    <Typo variant="h1">{heading}</Typo>
                    {intro ? (
                        <div
                            className={styles.intro}
                            dangerouslySetInnerHTML={{
                                __html: safeHtml(intro),
                            }}
                        />
                    ) : (
                        <p className={styles.intro}>{topicIntro(page)}</p>
                    )}
                    <DirectoryNotes
                        updatedAt={pageUpdatedAt(page)}
                        order="Ordered by the most recent press coverage on this topic; brokers quoted only in a Newspage story come after."
                    />
                </div>

                {brokers.length ? (
                    <ul className={styles.list}>
                        {brokers.map((broker) => (
                            <BrokerCard key={broker.slug} broker={broker} />
                        ))}
                    </ul>
                ) : (
                    <p className={styles.empty}>
                        No brokers are listed on this topic yet.
                    </p>
                )}

                <DirectoryStories stories={stories} label={topic.name} />

                <p className={styles.backLink}>
                    <Link href={MORTGAGE_BROKERS_PATH}>
                        <a>All mortgage broker topics</a>
                    </Link>
                </p>
            </div>
        </SidebarLayout>
    );
}
